import SearchField from "@amzn/meridian/search-field";
import Select, { SelectOption } from "@amzn/meridian/select";
import alertTokens from "@amzn/meridian-tokens/base/icon/alert";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadingSpinner from "src/components/shared/loadingSpinner/loadingSpinner";
import PaginationWithDropdown from "src/components/shared/paginationWithDropdown/paginationWithDropdown";
import { DEFAULT_TABLE_PAGE_SIZE } from "src/constants/constants";
import { getCurrentRegion } from "src/utils/regionUtils";
import { filterTableData, paginateTableData } from "src/utils/tableUtils";
import {
  CHANGE_TYPE_LABELS,
  CHANGE_TYPES,
  FILE_TYPE_LABELS,
  FILE_TYPES,
  isRegionCode,
  matchesChangeFilter,
  matchesCoverageFilter,
  REPORT_REGION_ORDER,
  regionShortLabel,
  sortRows,
  uncoveredDocumentCount,
} from "./adminReportUtils";
import Chip from "./components/chip";
import DocumentsSummary from "./components/documentsSummary";
import DocumentsTable from "./components/documentsTable";
import LoadError from "./components/loadError";
import ReportPageHeader from "./components/reportPageHeader";
import adminReportService from "./service/adminReportService";
import {
  ChangeFilter,
  CoverageFilter,
  FileType,
  PolicyDocument,
  RegionCode,
} from "./types";

/** Falls back to the realm the site is served from when no region is in the URL. */
const resolveRegion = (regionParam: string | null): RegionCode => {
  if (regionParam && isRegionCode(regionParam)) return regionParam;
  const current = getCurrentRegion();
  return isRegionCode(current) ? current : "na";
};

const COVERAGE_FILTERS: CoverageFilter[] = ["all", "covered", "uncovered"];

const isCoverageFilter = (value: string): value is CoverageFilter =>
  COVERAGE_FILTERS.includes(value as CoverageFilter);

const DocumentsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const region = resolveRegion(searchParams.get("region"));
  const coverageParam = searchParams.get("coverage");

  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchField, setSearchField] = useState("");
  const [changeFilter, setChangeFilter] = useState<ChangeFilter>("all");
  const [fileTypeFilter, setFileTypeFilter] = useState<FileType | "all">("all");
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>(
    coverageParam && isCoverageFilter(coverageParam) ? coverageParam : "all",
  );
  const [sortColumn, setSortColumn] =
    useState<keyof PolicyDocument>("fileName");
  const [sortDirection, setSortDirection] = useState("ascending");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);

  const loadDocuments = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      setDocuments(await adminReportService.getDocuments(region));
    } catch {
      setDocuments([]);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [region]);

  // Deep link from the Overview uncovered card.
  useEffect(() => {
    if (coverageParam && isCoverageFilter(coverageParam)) {
      setCoverageFilter(coverageParam);
    }
  }, [coverageParam]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchField,
    changeFilter,
    fileTypeFilter,
    coverageFilter,
    perPage,
    region,
  ]);

  const filteredDocuments = useMemo(
    () =>
      documents.filter(
        (document) =>
          matchesChangeFilter(document, changeFilter) &&
          matchesCoverageFilter(document, coverageFilter) &&
          (fileTypeFilter === "all" || document.fileType === fileTypeFilter),
      ),
    [documents, changeFilter, coverageFilter, fileTypeFilter],
  );

  // Kept separate from pagination so the summary counts reflect the search too.
  const matchingDocuments = sortRows(
    filterTableData({ tableData: filteredDocuments, searchField }),
    sortColumn,
    sortDirection,
  );

  const { paginatedTableData, totalPages } = paginateTableData({
    tableData: matchingDocuments,
    currentPage,
    pageSize: perPage,
  });

  const onSort = (field: keyof PolicyDocument) => {
    if (field === sortColumn) {
      setSortDirection(
        sortDirection === "ascending" ? "descending" : "ascending",
      );
    } else {
      setSortColumn(field);
      setSortDirection("ascending");
    }
    setCurrentPage(1);
  };

  const onRegionChange = (value: RegionCode) =>
    setSearchParams({ region: value });

  const uncovered = uncoveredDocumentCount(matchingDocuments);

  return (
    <div className="admin-report">
      <ReportPageHeader
        actions={
          <Select
            value={region}
            onChange={onRegionChange}
            size="small"
            width={120}
            aria-label="Region"
          >
            {REPORT_REGION_ORDER.map((code) => (
              <SelectOption
                key={code}
                value={code}
                label={regionShortLabel(code)}
              />
            ))}
          </Select>
        }
      />

      <div className="admin-report__body">
        <section className="admin-report__card">
          {!isLoading && (
            <DocumentsSummary documents={documents} region={region} />
          )}

          <div className="admin-report__filters">
            <div className="admin-report__filter admin-report__filter--search">
              <SearchField
                value={searchField}
                onChange={setSearchField}
                onSubmit={() => {}}
                placeholder="Search by file name, policy ID or policy title..."
                size="small"
                width="100%"
              />
            </div>
            <div className="admin-report__filter">
              <Select
                value={changeFilter}
                onChange={setChangeFilter}
                size="small"
                width="100%"
                label="Change Type"
              >
                <SelectOption value="all" label="All change types" />
                {CHANGE_TYPES.map((changeType) => (
                  <SelectOption
                    key={changeType}
                    value={changeType}
                    label={CHANGE_TYPE_LABELS[changeType]}
                  />
                ))}
              </Select>
            </div>
            <div className="admin-report__filter">
              <Select
                value={fileTypeFilter}
                onChange={setFileTypeFilter}
                size="small"
                width="100%"
                label="File type"
              >
                <SelectOption value="all" label="All file types" />
                {FILE_TYPES.map((fileType) => (
                  <SelectOption
                    key={fileType}
                    value={fileType}
                    label={FILE_TYPE_LABELS[fileType]}
                  />
                ))}
              </Select>
            </div>
            <div className="admin-report__filter">
              <Select
                value={coverageFilter}
                onChange={setCoverageFilter}
                size="small"
                width="100%"
                label="Coverage"
              >
                <SelectOption value="all" label="All coverage" />
                <SelectOption value="covered" label="Covered" />
                <SelectOption value="uncovered" label="Uncovered" />
              </Select>
            </div>
          </div>

          <div className="admin-report__list-summary">
            <div className="admin-report__list-count">
              {`${matchingDocuments.length} document${
                matchingDocuments.length === 1 ? "" : "s"
              }`}
              {uncovered > 0 && (
                <Chip tone="red" tokens={alertTokens}>
                  {`${uncovered} uncovered`}
                </Chip>
              )}
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : hasError ? (
            <div className="admin-report__card-body">
              <LoadError subject="documents" onRetry={loadDocuments} />
            </div>
          ) : (
            <DocumentsTable
              documents={paginatedTableData}
              region={region}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
              onDocumentClick={(attachmentId) =>
                navigate(`/admin/documents/${attachmentId}?region=${region}`)
              }
            />
          )}
        </section>

        {!isLoading && matchingDocuments.length > perPage && (
          <PaginationWithDropdown
            totalResults={matchingDocuments.length}
            totalPages={totalPages}
            currentPage={currentPage}
            perPage={perPage}
            setPerPage={setPerPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentsList;
